import { useMemo, useState, type ReactNode } from 'react';
import {
	Box,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	Portal,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import {
	CheckboxMenuSearch,
	CheckboxMenuRow,
	CheckboxMenuList,
	CheckboxMenuFooter,
	CheckboxMenuTrigger,
	MenuRow,
	checkboxMenuPopoverContentSx,
	checkboxMenuColors,
} from './CheckboxMenu';

/**
 * Dropdown / Combobox — the single button-opens-a-list selector that unifies
 * the previous LocationSelector / VendorSelector / PopoverSelector /
 * MetricSelector patterns.
 *
 * Contract:
 * - single-select: `value: string`, `onChange: (value) => void`
 * - multi-select: pass `multiple`, `value: string[]`, `onChange: (string[])`
 * - `searchable`: shows a filter input when there are many options
 *
 * Visual language: ENG-2279 variant 1e (tokens + search + rounded-square checks).
 */
export interface DropdownOption<T extends string = string> {
	value: T;
	label: ReactNode;
	/** Plain text used for search filtering when `label` is a node. */
	searchText?: string;
	isDisabled?: boolean;
}

interface BaseProps<T extends string> {
	options: DropdownOption<T>[];
	placeholder?: string;
	searchable?: boolean;
	size?: 'sm' | 'md';
	isDisabled?: boolean;
	buttonProps?: Record<string, unknown>;
	matchWidth?: boolean;
}

type SingleProps<T extends string> = BaseProps<T> & {
	multiple?: false;
	value: T | null;
	onChange: (value: T) => void;
};

type MultiProps<T extends string> = BaseProps<T> & {
	multiple: true;
	value: T[];
	onChange: (value: T[]) => void;
};

export type DropdownProps<T extends string = string> = SingleProps<T> | MultiProps<T>;

export function Dropdown<T extends string = string>(props: DropdownProps<T>) {
	const {
		options,
		placeholder = 'Select',
		searchable = false,
		size = 'md',
		isDisabled,
		buttonProps,
		matchWidth = true,
	} = props;
	const { isOpen, onToggle, onClose } = useDisclosure();
	const [query, setQuery] = useState('');

	const selectedValues = useMemo<T[]>(
		() => (props.multiple ? props.value : props.value ? [props.value] : []),
		[props.multiple, props.value],
	);

	const filtered = useMemo(() => {
		if (!searchable || !query.trim()) return options;
		const q = query.toLowerCase();
		return options.filter((o) =>
			(o.searchText ?? (typeof o.label === 'string' ? o.label : o.value))
				.toLowerCase()
				.includes(q),
		);
	}, [options, query, searchable]);

	const tokenLabels = useMemo(
		() =>
			selectedValues
				.map((v) => {
					const opt = options.find((o) => o.value === v);
					return typeof opt?.label === 'string' ? opt.label : String(v);
				}),
		[options, selectedValues],
	);

	const handleSelect = (value: T) => {
		if (props.multiple) {
			const set = new Set(props.value);
			if (set.has(value)) set.delete(value);
			else set.add(value);
			props.onChange(Array.from(set));
		} else {
			props.onChange(value);
			onClose();
		}
	};

	return (
		<Popover
			isOpen={isOpen}
			onClose={() => {
				setQuery('');
				onClose();
			}}
			matchWidth={matchWidth}
			placement="bottom-start"
			gutter={8}
		>
			<PopoverTrigger>
				<Box {...buttonProps}>
					<CheckboxMenuTrigger
						isOpen={isOpen}
						tokens={props.multiple ? tokenLabels : (tokenLabels.length ? tokenLabels : [])}
						emptyLabel={placeholder}
						onClick={isDisabled ? undefined : onToggle}
						// `sm` matches the 2rem form controls (size="sm" inputs).
						{...(size === 'sm' ? { minH: '2rem', py: '1px' } : {})}
						{...(isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})}
					/>
				</Box>
			</PopoverTrigger>
			<Portal>
				{/* matchWidth: popper only sets --popper-reference-width on the
				    positioner; the content must consume it, or it falls back to the
				    theme's default width (xs) and ignores the trigger's size. */}
				<PopoverContent
					w={matchWidth ? 'var(--popper-reference-width)' : undefined}
					minW={matchWidth ? undefined : '220px'}
					maxW={matchWidth ? undefined : '360px'}
					sx={checkboxMenuPopoverContentSx}
				>
					<PopoverBody p={0}>
						{searchable ? (
							<CheckboxMenuSearch
								value={query}
								onChange={setQuery}
								placeholder="Search..."
							/>
						) : null}
						<CheckboxMenuList>
							{filtered.length === 0 ? (
								<Text fontSize="13px" color={checkboxMenuColors.muted} px="10px" py="8px">
									No results
								</Text>
							) : props.multiple ? (
								filtered.map((opt) => (
									<CheckboxMenuRow
										key={opt.value}
										isSelected={selectedValues.includes(opt.value)}
										isDisabled={opt.isDisabled}
										onClick={() => handleSelect(opt.value)}
									>
										{opt.label}
									</CheckboxMenuRow>
								))
							) : (
								filtered.map((opt) => (
									<MenuRow
										key={opt.value}
										kind="single"
										isSelected={selectedValues.includes(opt.value)}
										isDisabled={opt.isDisabled}
										onClick={() => handleSelect(opt.value)}
									>
										{opt.label}
									</MenuRow>
								))
							)}
						</CheckboxMenuList>
						{props.multiple ? (
							<CheckboxMenuFooter
								selectedCount={selectedValues.length}
								totalCount={options.length}
								onClear={() => props.onChange([])}
							/>
						) : null}
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	);
}

export default Dropdown;
